import {Box, Paper, Stack} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {SurveyBannerVariant, SurveyContentAlignment, SurveyTheme} from "crqlar-react-common/dist/models/survey/survey.model";
import React from "react";
import {SurveyProgress} from "../shared/surveyPages.util";
import {surveyQuestionLayout, surveySolidColors} from "./surveyRunnerColors";
import SurveyRunnerProgress from "./SurveyRunnerProgress";
import SurveyTextOn from "./SurveyTextOn";

interface Props {
    theme?: SurveyTheme
    progress?: SurveyProgress
    /** The survey's language, which the progress bar's screen-reader text is written in. */
    language?: string
    reserveProgressSpace?: boolean
    progressLabel?: string
    showLogo?: boolean
    footer?: React.ReactNode
    children: React.ReactNode
}

const CONTENT_JUSTIFY: Record<SurveyContentAlignment, string> = {
    [SurveyContentAlignment.TOP]: 'flex-start',
    [SurveyContentAlignment.CENTER]: 'center',
    [SurveyContentAlignment.BOTTOM]: 'flex-end',
};

const CONTENT_MAX_WIDTH = 680;

function SurveyRunnerFrame({
    theme,
    progress,
    language,
    reserveProgressSpace,
    progressLabel,
    showLogo,
    footer,
    children
}: Props) {
    const runnerTheme = useTheme();
    const bannerFullWidth = theme?.bannerVariant === SurveyBannerVariant.FULL_WIDTH;
    const justifyContent = CONTENT_JUSTIFY[theme?.contentAlignment ?? SurveyContentAlignment.TOP];

    // Plain questions whose text would not read on the page share one sheet in the card surface colour.
    const {plain, textOnPage} = surveyQuestionLayout(runnerTheme);
    const sheet = plain && !textOnPage;

    const banner = !!theme?.bannerMedia?.mediaURI && (
        <Box
            component={'img'}
            src={theme.bannerMedia.mediaURI}
            alt={''}
            sx={{
                display: 'block',
                width: '100%',
                maxHeight: 220,
                objectFit: 'cover',
                ...(bannerFullWidth ? {} : {borderRadius: 1}),
            }}
        />
    );

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {(reserveProgressSpace || progress !== undefined) && (
                // The page colour, solid, so the questions scroll under the header cleanly.
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        flexShrink: 0,
                        px: 2,
                        bgcolor: surveySolidColors(runnerTheme).page,
                    }}
                >
                    <Box sx={{width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto'}}>
                        <SurveyRunnerProgress
                            progress={progress}
                            language={language}
                            label={progressLabel}
                            variant={theme?.progressStyle}
                        />
                    </Box>
                </Box>
            )}

            {!!theme?.backgroundMedia?.mediaURI && (
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 0,
                        pointerEvents: 'none',
                        backgroundImage: `url(${theme.backgroundMedia.mediaURI})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: theme.backgroundOpacity ?? 1,
                    }}
                />
            )}

            <Box sx={{position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column'}}>
                {bannerFullWidth && banner}

                <Box
                    sx={{
                        width: '100%',
                        py: {xs: 3, sm: 6},
                        px: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent,
                    }}
                >
                    <Stack gap={3} sx={{width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto'}}>
                        {!bannerFullWidth && banner}

                        {showLogo !== false && !!theme?.logoMedia?.mediaURI && (
                            <Box
                                component={'img'}
                                src={theme.logoMedia.mediaURI}
                                alt={''}
                                sx={{display: 'block', maxHeight: 72, maxWidth: '60%', mx: 'auto'}}
                            />
                        )}

                        {sheet ? (
                            <Paper
                                elevation={0}
                                sx={{p: {xs: 2.5, sm: 4}, border: '1px solid', borderColor: 'divider'}}
                            >
                                <Stack gap={3}>
                                    {children}
                                </Stack>
                            </Paper>
                        ) : <SurveyTextOn on={'page'}>{children}</SurveyTextOn>}
                    </Stack>
                </Box>

                {!!footer && (
                    <Box sx={{width: '100%', px: 2, pb: {xs: 2, sm: 3}}}>
                        <Stack gap={2} sx={{width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto'}}>
                            <SurveyTextOn on={'page'}>{footer}</SurveyTextOn>
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default SurveyRunnerFrame;
